import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Newspaper, Eye, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function DigestPanel() {
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/digest/preview").then((r) => setP(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`Digest delivered to ${data.managers} manager inbox(es)${data.sent ? ` · ${data.sent} emailed` : data.failed ? ` · email failed (${data.failed}) — check domain verification` : ""}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send digest"); }
    finally { setBusy(false); }
  };

  if (!p) return null;
  const d = p.data;
  return (
    <Card data-testid="digest-panel" className="p-5 bg-white border-slate-200 mt-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-display font-bold text-[#0A2A5E]"><Newspaper className="w-4 h-4 text-[#C7761A]" /> Weekly digest</div>
          <p className="text-xs text-slate-500 mt-1">Every Monday, opted-in managers get one email (from LUMIS HR App) plus an inbox copy: open compliance windows, high-risk chats, and team quiz progress.
            {p.last && <> Last sent {new Date(p.last.created_at).toLocaleString()} ({p.last.mode}, {p.last.sent} emailed{p.last.failed ? `, ${p.last.failed} failed` : ""}).</>}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="digest-preview-btn" onClick={() => setOpen(true)} className="h-8 text-xs"><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
          <Button size="sm" data-testid="digest-send-btn" onClick={sendNow} disabled={busy} className="h-8 text-xs bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send now</>}</Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="p-2 rounded-lg bg-[#F5F9FE]"><div className="font-display text-xl font-extrabold text-[#0A2A5E]" data-testid="digest-compliance-count">{d.compliance.length}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">windows ≤14d</div></div>
        <div className="p-2 rounded-lg bg-rose-50"><div className="font-display text-xl font-extrabold text-rose-700" data-testid="digest-risk-count">{d.high_risk_count}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">high-risk chats</div></div>
        <div className="p-2 rounded-lg bg-[#FFF7E8]"><div className="font-display text-xl font-extrabold text-[#7C3F00]" data-testid="digest-quiz-count">{d.quiz.new_passes}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">quiz passes</div></div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="digest-preview-dialog">
          <DialogHeader><DialogTitle className="font-display">Weekly digest preview</DialogTitle><DialogDescription>Exactly what managers receive by email from "LUMIS HR App &lt;info@lumishrsolutions.com&gt;".</DialogDescription></DialogHeader>
          <iframe title="digest" srcDoc={p.html} className="w-full h-[70vh] rounded-lg border border-slate-200 bg-[#F5F9FE]" sandbox="" />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
